import React, { useState, useEffect } from 'react';
import {
    Box,
    TableCell,
    TableRow,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Card,
    CardMedia,
    Grid,
    Divider
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Refresh as RefreshIcon,
    PhotoCamera as PhotoIcon,
    Gesture as SignatureIcon,
    Description as PdfIcon
} from '@mui/icons-material';
import { AppTable } from '../../../shared/components/AppTable';
import { Loader } from '../../../shared/components/Loader';
import { getDeliveryProofList, DeliveryProof as DeliveryProofType } from '../../../shared/services/deliveryProofService';
import moment from 'moment';
import { resolveImageUrl } from '../../../shared/utils/imageUrl';

export const DeliveryProof: React.FC = () => {
    const [proofs, setProofs] = useState<DeliveryProofType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [selectedProof, setSelectedProof] = useState<DeliveryProofType | null>(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        loadProofs();
    }, []);

    const loadProofs = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getDeliveryProofList();
            if (response.success) {
                setProofs(response.data || []);
            } else {
                setProofs([]);
            }
        } catch (err: any) {
            console.error('Error loading delivery proofs:', err);
            setError(err.response?.data?.message || 'Failed to load delivery proofs');
            setProofs([]);
        } finally {
            setLoading(false);
        }
    };

    const handleViewClick = (proof: DeliveryProofType) => {
        setSelectedProof(proof);
        setViewDialogOpen(true);
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Delivery Proofs
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0">
                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={loadProofs}
                        disabled={loading}
                        sx={{
                            backgroundColor: '#0EA5E9',
                            '&:hover': { backgroundColor: '#0284C7' }
                        }}
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : proofs.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">No delivery proofs found.</Alert>
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100 dark:bg-gray-800">
                                    <TableCell className="!font-semibold">Order Info</TableCell>
                                    <TableCell className="!font-semibold">Status</TableCell>
                                    <TableCell className="!font-semibold">Agent</TableCell>
                                    <TableCell className="!font-semibold">Uploaded At</TableCell>
                                    <TableCell className="!font-semibold">Proofs</TableCell>
                                    <TableCell className="!font-semibold" align="right">Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={proofs.length === 0}
                            emptyColSpan={6}
                        >
                            {proofs.map((proof) => (
                                <TableRow key={proof.orderId} hover>
                                    <TableCell>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {proof.orderNumber}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={proof.orderStatus}
                                            size="small"
                                            color={proof.orderStatus === 'DELIVERED' ? 'success' : 'primary'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {proof.assignedTo?.name || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {proof.assignedTo?.mobile || ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {proof.deliveryProof?.photoUploadedAt
                                                ? moment(proof.deliveryProof.photoUploadedAt).format('DD MMM YYYY, hh:mm A')
                                                : proof.deliveryProof?.signatureUploadedAt
                                                    ? moment(proof.deliveryProof.signatureUploadedAt).format('DD MMM YYYY, hh:mm A')
                                                    : 'N/A'
                                            }
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={1}>
                                            {proof.deliveryProof?.photoUrl && (
                                                <Tooltip title="Photo Available">
                                                    <PhotoIcon color="primary" fontSize="small" />
                                                </Tooltip>
                                            )}
                                            {proof.deliveryProof?.signatureUrl && (
                                                <Tooltip title="Signature Available">
                                                    <SignatureIcon color="secondary" fontSize="small" />
                                                </Tooltip>
                                            )}
                                            {proof.deliveryProof?.signaturePdfUrl && (
                                                <Tooltip title="PDF Available">
                                                    <PdfIcon color="error" fontSize="small" />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View Proofs">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewClick(proof)}
                                                sx={{ color: '#0EA5E9' }}
                                            >
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>
            </div>

            {/* View Proof Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Delivery Proof - {selectedProof?.orderNumber}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Delivery Photo
                            </Typography>
                            {selectedProof?.deliveryProof?.photoUrl ? (
                                <Card variant="outlined">
                                    <CardMedia
                                        component="img"
                                        image={resolveImageUrl(selectedProof.deliveryProof.photoUrl) || ''}
                                        alt="Delivery Photo"
                                        sx={{ height: 300, objectFit: 'contain', bgcolor: '#f5f5f5' }}
                                    />
                                    <Box p={2}>
                                        <Typography variant="caption" color="textSecondary">
                                            Uploaded: {moment(selectedProof.deliveryProof.photoUploadedAt).format('DD MMM YYYY, hh:mm A')}
                                        </Typography>
                                    </Box>
                                </Card>
                            ) : (
                                <Box p={4} border="1px dashed #ccc" borderRadius={1} textAlign="center">
                                    <Typography color="textSecondary">No photo uploaded</Typography>
                                </Box>
                            )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Customer Signature
                            </Typography>
                            {selectedProof?.deliveryProof?.signatureUrl ? (
                                <Card variant="outlined">
                                    <CardMedia
                                        component="img"
                                        image={resolveImageUrl(selectedProof.deliveryProof.signatureUrl) || ''}
                                        alt="Signature"
                                        sx={{ height: 300, objectFit: 'contain', bgcolor: '#f5f5f5' }}
                                    />
                                    <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="caption" color="textSecondary">
                                            Uploaded: {moment(selectedProof.deliveryProof.signatureUploadedAt).format('DD MMM YYYY, hh:mm A')}
                                        </Typography>
                                        {selectedProof.deliveryProof.signaturePdfUrl && (
                                            <Button
                                                size="small"
                                                startIcon={<PdfIcon />}
                                                href={resolveImageUrl(selectedProof.deliveryProof.signaturePdfUrl)}
                                                target="_blank"
                                                color="error"
                                            >
                                                Download PDF
                                            </Button>
                                        )}
                                    </Box>
                                </Card>
                            ) : (
                                <Box p={4} border="1px dashed #ccc" borderRadius={1} textAlign="center">
                                    <Typography color="textSecondary">No signature uploaded</Typography>
                                </Box>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Box mt={2}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Agent Details
                                </Typography>
                                <Typography variant="body2">
                                    Name: {selectedProof?.assignedTo?.name || 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    Mobile: {selectedProof?.assignedTo?.mobile || 'N/A'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen || !!error}
                autoHideDuration={6000}
                onClose={() => { setSnackbarOpen(false); setError(''); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => { setSnackbarOpen(false); setError(''); }} severity={error ? "error" : "success"}>
                    {error || snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};
